import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { LuCheck } from "react-icons/lu";
import { useConsentApi } from "../../store/api/hooks/consentApiHook";
import FormattedDate from "../elements/FormattedDate";
import CommunicationPreferences from "./CommunicationPreferences";

interface MerchantConsent {
  accepted?: boolean;
  acceptedBy?: { displayName?: string | null } | null;
  acceptedAt?: string | null;
  acceptedVia?: string | null;
}

/** Owner only: read-only terms acceptance record + their own optional SMS consent. */
const AgreementsPoliciesCard: React.FC<{ enabled: boolean }> = ({ enabled }) => {
  const { t } = useTranslation("consent");
  const consentApi = useConsentApi();
  const [data, setData] = useState<MerchantConsent | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  const load = useCallback(() => {
    setIsLoading(true);
    setIsError(false);
    consentApi.getMerchantConsent(
      {},
      (resp: any) => {
        setData(resp?.data ?? null);
        setIsLoading(false);
      },
      () => {
        setIsError(true);
        setIsLoading(false);
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (enabled) load();
  }, [enabled, load]);

  const via = data?.acceptedVia?.toUpperCase();
  const viaLabel = via === "MERCHANT_ADMIN" ? t("via.merchantAdmin") : via === "POS" ? t("via.pos") : data?.acceptedVia || "—";

  const rows: [string, React.ReactNode][] = [
    [t("record.agreement"), t("record.agreementValue")],
    [t("record.acceptedBy"), data?.acceptedBy?.displayName ? `${data.acceptedBy.displayName} (${t("modal.ownerBadge")})` : "—"],
    [t("record.acceptedAt"), data?.acceptedAt ? <FormattedDate date={data.acceptedAt} format="monthDayYear" /> : "—"],
    [t("record.acceptedVia"), viaLabel],
  ];

  return (
    <div className="p-4 space-y-3 bg-white border border-gray-200 rounded-xl">
      <h4 className="font-semibold text-gray-900">{t("record.title")}</h4>

      <div className="p-3 border border-gray-200 rounded-lg">
        <div className="flex items-center gap-2 mb-3">
          <h5 className="text-md font-semibold text-gray-900">{t("terms.title")}</h5>
          <span className="px-2 py-0.5 text-[11px] font-medium text-red-600 bg-red-50 rounded-full">{t("mandatory")}</span>
        </div>
        {isLoading && !data && (
          <div className="space-y-2.5">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="flex items-center justify-between">
                <div className="w-1/3 h-3.5 rounded bg-gray-100 animate-pulse" />
                <div className="w-2/5 h-3.5 rounded bg-gray-100 animate-pulse" />
              </div>
            ))}
          </div>
        )}
        {isError && (
          <p className="text-xs text-red-600" role="alert">
            {t("record.loadError")}{" "}
            <button type="button" className="font-medium underline" onClick={load}>
              {t("retry")}
            </button>
          </p>
        )}
        {data && (
          <>
            {data.accepted && (
              <div className="flex items-center gap-1.5 px-3 py-2 mb-3 text-xs font-medium text-green-700 rounded-lg bg-green-50">
                <LuCheck size={14} /> {t("record.activeForAll")}
              </div>
            )}
            <div className="space-y-2 text-sm">
              {rows.map(([label, value]) => (
                <div key={label} className="flex justify-between gap-3">
                  <span className="text-gray-500">{label}</span>
                  <span className="text-right text-gray-900">{value}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      <CommunicationPreferences enabled={enabled} />
    </div>
  );
};

export default AgreementsPoliciesCard;
